'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { toExternalHref } from '@up2stay/up2promo-commons';
import { ChevronRight, ExternalLink } from 'lucide-react';

import { useProfileLayout } from '../layouts/profile-layout-context';
import {
  ModuleEntryCard,
  ModuleEntryIcon,
  ModuleEntryRow,
  ModuleEntryTitleBlock,
} from './module-entry-card';

interface LinksModuleDisplayProps {
  module: ProfileModuleDto;
}

type LinkItem = {
  id?: string;
  title: string;
  url: string;
  description?: string;
  icon?: string;
  openInNewTab?: boolean;
  order?: number;
  visible?: boolean;
};

type LinksModuleConfig = {
  type: 'links';
  items: LinkItem[];
  layout?: 'grid' | 'list';
};

const DESCRIPTION_DISPLAY_MAX_LENGTH = 200;

function getLinksConfig(rawConfig: Record<string, unknown>): LinksModuleConfig {
  const rawLayout = rawConfig.layout as string | undefined;
  const layout = rawLayout === 'grid' ? 'grid' : 'list';
  return {
    type: 'links',
    items: (rawConfig.items as LinkItem[]) || [],
    layout,
  };
}

function capDescription(text: string): string {
  if (text.length <= DESCRIPTION_DISPLAY_MAX_LENGTH) return text;
  return `${text.slice(0, DESCRIPTION_DISPLAY_MAX_LENGTH)}…`;
}

/**
 * Displays a list of links. Always opens in new tab, always Link icon, description capped.
 */
export function LinksModuleDisplay({ module }: LinksModuleDisplayProps) {
  const profileLayout = useProfileLayout();
  const config = getLinksConfig(module.config || {});

  const visibleItems = (config.items || [])
    .filter((item) => item.visible !== false && item.url)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (visibleItems.length === 0) {
    return null;
  }

  if (profileLayout === 'centered') {
    return (
      <div className="flex w-full flex-col gap-2.5">
        {visibleItems.map((item, index) => (
          <a
            key={item.id ?? index}
            href={toExternalHref(item.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-11 w-full items-center justify-center gap-2.5 rounded-full border border-[var(--border)] bg-[var(--surface)] px-4 py-4 text-[14px] font-semibold text-[var(--text)]"
          >
            <ExternalLink
              className="h-[18px] w-[18px]"
              style={{ stroke: 'var(--accent)' }}
              strokeWidth={1.7}
            />
            {item.title}
          </a>
        ))}
      </div>
    );
  }

  if (profileLayout === 'classic') {
    return (
      <div className="mt-3.5 flex w-full min-w-0 flex-wrap gap-[9px] @min-[880px]/profile:mt-0 @min-[880px]/profile:grid @min-[880px]/profile:grid-cols-2 @min-[880px]/profile:gap-3">
        {visibleItems.map((item, index) => (
          <a
            key={item.id ?? index}
            href={toExternalHref(item.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-full border-[1.5px] border-[var(--border)] px-[15px] py-2.5 text-[13px] font-medium text-[var(--text)] @min-[880px]/profile:w-full @min-[880px]/profile:justify-start @min-[880px]/profile:rounded-[14px] @min-[880px]/profile:px-[18px] @min-[880px]/profile:py-4 @min-[880px]/profile:text-[14px] @min-[880px]/profile:font-semibold"
          >
            <ExternalLink
              className="h-[15px] w-[15px] shrink-0 @min-[880px]/profile:h-[18px] @min-[880px]/profile:w-[18px]"
              style={{ stroke: 'var(--accent)' }}
              strokeWidth={1.7}
            />
            <span className="min-w-0 truncate">{item.title}</span>
          </a>
        ))}
      </div>
    );
  }

  const layout = config.layout === 'grid' ? 'grid' : 'list';

  return (
    <div
      className={
        layout === 'grid'
          ? 'grid w-full grid-cols-1 gap-2.5 sm:grid-cols-2'
          : 'flex w-full flex-col gap-2.5'
      }
    >
      {visibleItems.map((item, index) => (
        <ModuleEntryCard key={item.id ?? index}>
          <a
            href={toExternalHref(item.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="block transition-colors"
          >
            <ModuleEntryRow>
              <ModuleEntryIcon>
                <ExternalLink strokeWidth={1.7} />
              </ModuleEntryIcon>
              <ModuleEntryTitleBlock
                title={item.title}
                description={
                  item.description
                    ? capDescription(item.description)
                    : undefined
                }
              />
              <ChevronRight
                className="h-[17px] w-[17px] shrink-0"
                style={{ stroke: 'var(--faint)' }}
                strokeWidth={1.8}
              />
            </ModuleEntryRow>
          </a>
        </ModuleEntryCard>
      ))}
    </div>
  );
}
